import React from "react";
import { TrendingUp, TrendingDown, Minus, Users, Target } from "lucide-react";

/**
 * UpsideAnchorCard — "how far can this go" for a ticker.
 *
 * Two panels side-by-side:
 *   1. Analyst Consensus (real Wall St data from Finnhub)
 *   2. Model-Estimated 45-day Range (our own math: vol σ + 52w + consensus tilt)
 *
 * Props:
 *   anchor: {
 *     symbol, entry_price, consensus, estimated_range: {...}
 *   } | null
 */
export default function UpsideAnchorCard({ anchor, entryLabel = "Current" }) {
  if (!anchor) return null;
  const c = anchor.consensus;
  const e = anchor.estimated_range;

  const ratingColor = {
    STRONG_BUY: "text-emerald-700 bg-emerald-50 border-emerald-200",
    BUY: "text-emerald-700 bg-emerald-50 border-emerald-200",
    HOLD: "text-amber-700 bg-amber-50 border-amber-200",
    SELL: "text-red-700 bg-red-50 border-red-200",
    STRONG_SELL: "text-red-800 bg-red-100 border-red-300",
  }[c?.rating] || "text-neutral-600 bg-neutral-100 border-neutral-200";

  const momentumIcon = (dir) => {
    if (dir === "up") return <TrendingUp className="w-4 h-4 text-emerald-600" data-testid="anchor-momentum-up" />;
    if (dir === "down") return <TrendingDown className="w-4 h-4 text-red-500" data-testid="anchor-momentum-down" />;
    return <Minus className="w-4 h-4 text-neutral-400" data-testid="anchor-momentum-flat" />;
  };

  return (
    <div className="mt-6 bg-white border border-mb-border rounded-lg overflow-hidden" data-testid="upside-anchor-card">
      <div className="px-6 py-4 border-b border-mb-border flex items-center justify-between">
        <div>
          <div className="label-uppercase text-neutral-500">Upside anchor</div>
          <div className="text-sm text-neutral-700 mt-0.5">How far can it go — Street view + our model</div>
        </div>
        <Target className="w-5 h-5 text-mb-emerald" />
      </div>

      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-mb-border">
        {/* Left: Analyst Consensus */}
        <div className="p-6" data-testid="anchor-consensus-panel">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-neutral-500" />
            <span className="label-uppercase text-neutral-500">Analyst consensus</span>
          </div>

          {c ? (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <span
                  className={`px-3 py-1 rounded-md border font-semibold text-sm ${ratingColor}`}
                  data-testid="anchor-rating"
                >
                  {c.rating.replace("_", " ")}
                </span>
                <span className="font-mono-data text-sm text-neutral-700" data-testid="anchor-analyst-count">
                  {c.analyst_count} analysts
                </span>
              </div>

              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-neutral-600">Bullish</span>
                  <span className="font-mono-data font-semibold text-emerald-700" data-testid="anchor-bull-pct">
                    {c.bull_pct}%
                  </span>
                </div>
                {/* Split bar */}
                <div className="h-2 rounded-full bg-neutral-100 overflow-hidden flex" data-testid="anchor-bull-bear-bar">
                  <div className="bg-emerald-500" style={{ width: `${c.bull_pct}%` }} />
                  <div className="bg-neutral-300" style={{ width: `${100 - c.bull_pct - c.bear_pct}%` }} />
                  <div className="bg-red-400" style={{ width: `${c.bear_pct}%` }} />
                </div>
                <div className="flex items-center justify-between text-xs text-neutral-500">
                  <span>Hold {(100 - c.bull_pct - c.bear_pct).toFixed(1)}%</span>
                  <span>Bearish {c.bear_pct}%</span>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-neutral-100 mt-3">
                  <span className="text-neutral-600 flex items-center gap-1.5">
                    {momentumIcon(c.momentum_3m?.direction)}
                    3-month momentum
                  </span>
                  <span className="font-mono-data text-xs text-neutral-700" data-testid="anchor-momentum">
                    {c.momentum_3m?.delta_bull_pct != null
                      ? `${c.momentum_3m.delta_bull_pct >= 0 ? "+" : ""}${c.momentum_3m.delta_bull_pct}pp bull`
                      : "—"}
                  </span>
                </div>
              </div>

              <div className="mt-3 text-[11px] text-neutral-400 uppercase tracking-wider" data-testid="anchor-consensus-detail">
                {c.counts.strong_buy} SB · {c.counts.buy} B · {c.counts.hold} H · {c.counts.sell} S · {c.counts.strong_sell} SS
              </div>
            </>
          ) : (
            <div className="text-sm text-neutral-500" data-testid="anchor-no-consensus">
              No analyst coverage tracked for this ticker.
            </div>
          )}
        </div>

        {/* Right: Model-Estimated Range */}
        <div className="p-6" data-testid="anchor-range-panel">
          <div className="flex items-center gap-2 mb-3">
            <span className="label-uppercase text-neutral-500">Model estimate ({e?.hold_days || 45}d)</span>
          </div>

          <div className="space-y-3">
            <CaseRow
              label="Bull case"
              pct={e?.bull_case_pct}
              price={e?.bull_case_price}
              tone="emerald"
              testid="anchor-bull-case"
            />
            <CaseRow
              label="Base case"
              pct={e?.base_case_pct}
              price={e?.base_case_price}
              tone="mint"
              testid="anchor-base-case"
            />
            <CaseRow
              label={e?.bear_label || "Bear case"}
              pct={e?.bear_case_pct}
              price={e?.bear_case_price}
              tone="red"
              testid="anchor-bear-case"
            />
          </div>

          <div className="mt-4 pt-3 border-t border-neutral-100 grid grid-cols-2 gap-2 text-[11px] text-neutral-500">
            <div>
              <div className="uppercase tracking-wider">Vol 1σ</div>
              <div className="font-mono-data text-neutral-700">{e?.one_sigma_pct}%</div>
            </div>
            <div>
              <div className="uppercase tracking-wider">52w range</div>
              <div className="font-mono-data text-neutral-700">
                {e?.lo_52w ? `$${e.lo_52w}` : "—"} – {e?.hi_52w ? `$${e.hi_52w}` : "—"}
              </div>
            </div>
          </div>

          <div className="mt-3 text-[10px] text-neutral-400 leading-relaxed" data-testid="anchor-method">
            Method: {e?.method || "n/a"}. Anchored on {entryLabel.toLowerCase()} price,
            σ scaled to {e?.hold_days || 45}d via √t; base ≈ +½σ, bull ≈ +1½σ capped at 110% of 52w high;
            bear line is the published stop{e?.bear_label === "Stop" ? "" : " (−1σ fallback when no stop set)"}.
          </div>
        </div>
      </div>
    </div>
  );
}

function CaseRow({ label, pct, price, tone, testid }) {
  if (pct == null) return null;
  const toneCls = {
    emerald: "text-emerald-700 bg-emerald-50",
    mint: "text-neutral-800 bg-neutral-50",
    red: "text-red-600 bg-red-50",
  }[tone];
  const sign = pct >= 0 ? "+" : "";
  return (
    <div className={`flex items-center justify-between px-3 py-2 rounded-md ${toneCls}`} data-testid={testid}>
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center gap-3">
        <span className="font-mono-data text-xs opacity-80">
          {sign}{pct}%
        </span>
        <span className="font-mono-data text-base font-semibold" data-testid={`${testid}-price`}>
          ${price}
        </span>
      </div>
    </div>
  );
}
